import {
  ActionOverRecordTypes,
  createFormModalConfig,
  createLocalizationKey,
  FieldBuilder,
  FieldDefinition,
  IActionsSchema,
  IModal,
  MenuCategories,
  MENU_CATEGORY_KEY,
  MENU_NAME_SUFFIX,
  MenuSubcategories,
  MENU_SUBCATEGORY_KEY,
  modalAction,
  triggerAction,
  ZendraLocalizationServices,
  ContractMasterGroupCodes,
} from '@zendra/shared';
import {
  CycleProjectionPlanningOrchestratorService,
  RotationProjectionPlanningOrchestratorService,
} from 'orchestrator';
import { BioForestAbpService } from 'orchestrator/proxy';
import {
  PropertyTypeService,
  VendorService,
  ForestryStatusService,
  InternalOrderService,
  MeasurementTypeService,
  ProductivityParameterService,
  StandService,
  PropertyService,
  CostReceiverService,
  ForestrySituationService,
  SpeciesService,
  GeneticMaterialTypeService,
  ZoneService,
  RegionService,
  ContractTypeService,
} from 'cbd-proxies';
import { ActionAssignEucalyptusPercentage } from './action-assign-eucalyptus-percentage/action-assign-eucalyptus-percentage';
import { FomentContractLookupService } from './services/foment-contract-lookup.service';

FieldBuilder.setNamespace('Geographic');

export const STANDCYCLEROTATION_FIELDS: FieldDefinition[] = [
  FieldBuilder.date('plantationDate', 'Entity.Cycle.PlantationDate')
    .readOnly()
    .build(),
  FieldBuilder.number('standArea', 'Entity.Stand.StandArea')
    .decimal()
    .readOnly()
    .build(),
  FieldBuilder.foreignKey('forestrySituationId', 'Entity.ForestrySituation', ForestrySituationService)
    .display('name')
    .displayFromNested('forestrySituationName')
    .sortBy('forestrySituationName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey('speciesId', 'Entity.Species', SpeciesService)
    .display('name')
    .displayFromNested('speciesName')
    .sortBy('speciesName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey('geneticMaterialTypeId', 'Entity.GeneticMaterialType', GeneticMaterialTypeService)
    .display('name')
    .displayFromNested('geneticMaterialTypeName')
    .sortBy('geneticMaterialTypeName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey('zoneId', 'Entity.Zone', ZoneService)
    .display('name')
    .displayFromNested('zoneName')
    .sortBy('zoneName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey('regionId', 'Entity.Region', RegionService)
    .display('name')
    .displayFromNested('regionName')
    .sortBy('regionName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey('fomentContractTypeId', 'Entity.StandCycleRotation.FomentContractType', ContractTypeService)
    .display('name')
    .displayFromNested('fomentContractTypeName')
    .sortBy('fomentContractTypeName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey('landContractTypeId', 'Entity.StandCycleRotation.LandContractType', ContractTypeService)
    .display('name')
    .displayFromNested('landContractTypeName')
    .sortBy('landContractTypeName')
    .readOnly()
    .build(),
  FieldBuilder.checkbox('locationIsActive', 'Entity.Location.IsActive')
    .readOnly()
    .build(),
  FieldBuilder.checkbox('isLastCycle', 'Entity.Cycle.IsLastCycle')
    .readOnly()
    .build(),
  FieldBuilder.checkbox('isLastRotation', 'Entity.Rotation.IsLastRotation')
    .readOnly()
    .build(),
  FieldBuilder
    .number('standCycleRotationId', 'Entity.StandCycleRotation.StandCycleRotationId')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey('standId', 'Entity.Stand', StandService)
    .display('name')
    .displayFromNested('standName')
    .sortBy('standName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey('propertyId', 'Entity.Property', PropertyService)
    .display('name')
    .displayFromNested('propertyName')
    .sortBy('propertyName')
    .prefilter({ multiple: true })
    .readOnly()
    .build(),
  FieldBuilder.foreignKey('costReceiverId', 'Entity.CostReceiver', CostReceiverService)
    .display('name')
    .displayFromNested('costReceiverName')
    .sortBy('costReceiverName')
    .readOnly()
    .build(),
  FieldBuilder
    .checkbox('isFOManagement', 'Entity.PropertyType.IsFOManagement')
    .readOnly()
    .build(),
  FieldBuilder
    .checkbox('isFscCertified', 'Entity.StandCycleRotation.IsFscCertified')
    .readOnly()
    .build(),
  FieldBuilder
    .checkbox('isPefcCertified', 'Entity.StandCycleRotation.IsPefcCertified')
    .readOnly()
    .build(),
  FieldBuilder
    .checkbox('isInventoryValidated', 'Entity.StandCycleRotation.IsInventoryValidated')
    .build(),
  FieldBuilder
    .number('dab', 'Entity.StandCycleRotation.Dab')
    .decimal()
    .build(),
  FieldBuilder
    .checkbox('applyFeller', 'Entity.StandCycleRotation.ApplyFeller')
    .build(),
  FieldBuilder
    .date('landDueDate', 'Entity.StandCycleRotation.LandDueDate')
    .readOnly()
    .build(),
  FieldBuilder
    .date('fomentWoodDueDate', 'Entity.StandCycleRotation.FomentWoodDueDate')
    .readOnly()
    .build(),
  FieldBuilder
    .number('nextCycleEstimatedAge', 'Entity.StandCycleRotation.NextCycleEstimatedAge')
    .decimal()
    .build(),
  FieldBuilder
    .number('eucalyptusPercentage', 'Entity.Cycle.EucalyptusPercentage')
    .decimal()
    .readOnly()
    .build(),
  FieldBuilder
    .number('eucalyptusArea', 'Entity.Cycle.EucalyptusArea')
    .decimal()
    .readOnly()
    .build(),
  FieldBuilder
    .number('dualPercentage', 'Entity.StandCycleRotation.DualPercentage')
    .required()
    .decimal()
    .build(),
  FieldBuilder
    .number('dualVolumeTree', 'Entity.StandCycleRotation.DualVolumeTree')
    .required()
    .decimal()
    .readOnly()
    .build(),
  FieldBuilder
    .checkbox('isStumpCandidateToRegrowth', 'Entity.StandCycleRotation.IsStumpCandidateToRegrowth')
    .build(),
  FieldBuilder
    .checkbox('isRegrowthCandidate', 'Entity.StandCycleRotation.IsRegrowthCandidate')
    .build(),
  FieldBuilder.foreignKey(
    'cycleId',
    'Entity.Cycle',
    CycleProjectionPlanningOrchestratorService
  )
    .display('cycleDisplay')
    .displayFromNested('cycleNumber')
    .sortBy('cycleNumber')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey(
    'rotationId',
    'Entity.StandCycleRotation.Rotation',
    RotationProjectionPlanningOrchestratorService
  )
    .required()
    .display('rotationDisplay')
    .displayFromNested('rotationNumber')
    .sortBy('rotationNumber')
    .build(),
  FieldBuilder.foreignKey(
    'propertyTypeId',
    'Entity.StandCycleRotation.PropertyType',
    PropertyTypeService
  )
    .display('name')
    .displayFromNested('propertyTypeName')
    .sortBy('propertyTypeName')
    .build(),
  FieldBuilder.foreignKey(
    'fomentContractId',
    'Entity.StandCycleRotation.FomentContract',
    FomentContractLookupService,
  )
    .keyField('fomentContractId')
    .display('number')
    .displayFromNested('fomentContractNumber')
    .sortBy('fomentContractNumber')
    .build(),
  FieldBuilder.foreignKey(
    'fomentVendorId',
    'Entity.StandCycleRotation.FomentVendor',
    VendorService,
  )
    .display('fantasyName')
    .displayFromNested('fomentVendorName')
    .sortBy('fomentVendorName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey(
    'forestryStatusId',
    'Entity.StandCycleRotation.ForestryStatus',
    ForestryStatusService
  )
    .display('name')
    .displayFromNested('forestryStatusName')
    .sortBy('forestryStatusName')
    .build(),
  FieldBuilder.foreignKey(
    'productivityParameterId',
    'Entity.StandCycleRotation.ProductivityParameter',
    ProductivityParameterService
  )
    .display('name')
    .displayFromNested('productivityParameterName')
    .sortBy('productivityParameterName')
    .build(),
  FieldBuilder.foreignKey(
    'internalOrderId',
    'Entity.StandCycleRotation.InternalOrder',
    InternalOrderService
  )
    .display('sapCode')
    .displayFromNested('internalOrderSapCode')
    .sortBy('internalOrderSapCode')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey(
    'landContractId',
    'Entity.StandCycleRotation.LandContract',
    BioForestAbpService,
  )
    .loadWith((service, query, config) => service.getContracts(query, undefined, config))
    .queryFilter({ contractMasterGroupCode: ContractMasterGroupCodes.Land })
    .display('number')
    .displayFromNested('landContractNumber')
    .sortBy('landContractNumber')
    .build(),
  FieldBuilder.foreignKey(
    'landVendorId',
    'Entity.StandCycleRotation.LandVendor',
    VendorService
  )
    .display('fantasyName')
    .displayFromNested('landVendorName')
    .sortBy('landVendorName')
    .readOnly()
    .build(),
  FieldBuilder.foreignKey(
    'measurementTypeId',
    'Entity.StandCycleRotation.MeasurementType',
    MeasurementTypeService
  )
    .display('name')
    .displayFromNested('measurementTypeName')
    .sortBy('measurementTypeName')
    .build(),
  FieldBuilder.foreignKey(
    'stumpApplicationId',
    'Entity.StandCycleRotation.StumpApplication',
    ProductivityParameterService
  )
    .display('name')
    .displayFromNested('stumpApplicationName')
    .sortBy('stumpApplicationName')
    .build(),
];

const namespace = ZendraLocalizationServices.Geographic;

export const STANDCYCLEROTATION_ACTION_IDS = {
  calculateDualVolumeTreeTrigger: 0,
  assignEucalyptusPercentageModal: 1,
};

const ACTION_ASSIGN_EUCALYPTUS_PERCENTAGE_WRAPPER: IModal = createFormModalConfig({
  component: ActionAssignEucalyptusPercentage,
  title: createLocalizationKey('Action.Modal.AssignEucalyptusPercentage', namespace),
  subTitle: createLocalizationKey('Entity.StandCycleRotation', namespace),
  confirmLabel: createLocalizationKey('Confirm', ZendraLocalizationServices.LanguageService),
});

export const STANDCYCLEROTATION_ACTIONS_CONFIG: IActionsSchema = {
  multipleRecord: [
    triggerAction(
      STANDCYCLEROTATION_ACTION_IDS.calculateDualVolumeTreeTrigger,
      'Action.Trigger.CalculateDualVolumeTree',
      ActionOverRecordTypes.MultipleRecord,
      namespace,
    ),
    modalAction(
      STANDCYCLEROTATION_ACTION_IDS.assignEucalyptusPercentageModal,
      'Action.Modal.AssignEucalyptusPercentage',
      ActionOverRecordTypes.MultipleRecord,
      ACTION_ASSIGN_EUCALYPTUS_PERCENTAGE_WRAPPER,
      namespace,
    ),
  ],
};

export const STANDCYCLEROTATION_CONFIG = {
  entityName: 'StandCycleRotation',
  localizationKey: 'Geographic::Entity.StandCycleRotation',
  fields: STANDCYCLEROTATION_FIELDS,
  permission: 'PlanningGeography.StandCycleRotations',
  actions: STANDCYCLEROTATION_ACTIONS_CONFIG,
  actionIds: STANDCYCLEROTATION_ACTION_IDS,
  showInMenu: true,
  menuCategory: MENU_CATEGORY_KEY + MenuCategories.Planning + MENU_NAME_SUFFIX,
  menuSubcategory: MENU_SUBCATEGORY_KEY + MenuSubcategories.TransactionsPlanning + MENU_NAME_SUFFIX,
};
